import type { Tutorial } from './types';

/**
 * Registre des tutoriels vidéo — liste explicite et ORDONNÉE : l'ordre du
 * tableau est l'ordre du parcours affiché sur `/tutoriels`, et correspond à la
 * numérotation des vidéos tournées (0 → 8).
 *
 * Deux temps, parce que la doc sert deux moments distincts :
 *
 * 1. `setup` — la configuration initiale, à suivre dans l'ordre (on ne remplit
 *    pas son agenda avant d'avoir un catalogue) ;
 * 2. `daily` — l'exploitation, où on revient piocher la section dont on a
 *    besoin (« j'ai oublié comment marchent les campagnes SMS »).
 *
 * **Le chapitrage est celui du montage réel**, relevé sur les vidéos tournées —
 * pas le découpage de la doc. Un chapitre couvre donc 1 à n fiches, une fiche
 * peut revenir dans deux chapitres, et certaines fiches ne sont couvertes par
 * aucun chapitre (elles retombent alors sur le tutoriel de leur section).
 *
 * Les `video` sont absentes tant que les vidéos ne sont pas publiées sur la
 * plateforme d'hébergement : la page reste en ligne avec son chapitrage.
 * Publier = ajouter le bloc `video`, rien d'autre.
 */
export const TUTORIALS: Tutorial[] = [
  {
    id: 'demarrage',
    phase: 'setup',
    title: 'Découvrir Propelsia',
    intent:
      "Le tour du menu et des grandes sections de Propelsia, pour savoir où se trouve quoi avant d'entrer dans le détail.",
    duration_s: 310,
    video: { provider: 'youtube', video_id: 'wiJ_KExikno', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Vous savez à quoi sert chaque section du menu et comment elles se répondent.',
      'Vous savez lire votre score de configuration et suivre les étapes recommandées.',
      'Vous savez où chercher : la recherche globale et Pulse, votre assistant.',
    ],
    chapters: [
      { at: '00:00', label: 'Bienvenue et tour du menu' },
      { at: '01:09', label: 'Le site internet et sa personnalisation' },
      { at: '01:49', label: 'La communication et les avis Google' },
      { at: '03:06', label: 'Les clients, l’agenda et les notifications' },
      { at: '03:54', label: 'L’accueil, la recherche globale et Pulse' },
    ],
  },
  {
    id: 'establishment',
    category: 'establishment',
    phase: 'setup',
    title: 'Configurer le profil Entreprise',
    intent:
      "Renseigner l'identité de votre entreprise, vos photos, votre équipe, vos lieux de rendez-vous, votre fiche Google et votre compte de paiement.",
    duration_s: 745,
    video: { provider: 'youtube', video_id: 'eTGkQxmN5oA', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Votre fiche publique est complète : informations, description, photos.',
      'Votre équipe (ou votre profil solo) est renseignée et sélectionnable à la réservation.',
      'Vos lieux sont déclarés — sur place, en déplacement ou en visio.',
      'Votre fiche Google est reliée et votre compte de paiement Stripe est connecté.',
    ],
    chapters: [
      {
        at: '00:00',
        label: 'Le profil : activité et informations légales',
        feature_refs: ['establishment.info.update'],
      },
      { at: '01:31', label: 'La description générée par Pulse et les liens externes' },
      { at: '02:21', label: 'Le logo et les photos', feature_refs: ['establishment.media.manage'] },
      { at: '02:49', label: 'L’équipe : profils, expertise, parcours', feature_refs: ['establishment.team.profile'] },
      { at: '04:02', label: 'Donner un accès et choisir un rôle', feature_refs: ['establishment.team.access'] },
      {
        at: '04:40',
        label: 'Les lieux : sur place, en déplacement, en visio',
        feature_refs: ['establishment.place.manage'],
      },
      {
        at: '07:33',
        label: 'La fiche Google et la réservation via Google',
        feature_refs: ['establishment.google_sync.connect'],
      },
      { at: '09:44', label: 'Pulse et la voix de marque', feature_refs: ['establishment.pulse.personalize'] },
      {
        at: '11:06',
        label: 'Le compte de paiement Stripe',
        feature_refs: ['establishment.payment.stripe_connect'],
      },
      // La vidéo termine sur l'abonnement et les factures : des fiches de la
      // catégorie `account`, qui n'a pas de tutoriel à elle.
      {
        at: '11:31',
        label: 'L’abonnement et les factures',
        feature_refs: ['account.subscription.manage', 'account.subscription.invoices'],
      },
    ],
  },
  {
    id: 'catalog',
    category: 'catalog',
    phase: 'setup',
    title: 'Configuration des offres : services, packs et cartes cadeaux',
    intent:
      'Structurer et créer tout ce que vous vendez : catégories, prestations, prix et durées, suppléments, matériel, packs et cartes cadeaux.',
    duration_s: 1139,
    video: { provider: 'youtube', video_id: 'I91nqWq7zEQ', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Votre offre est rangée en catégories lisibles par vos clients.',
      'Vos prestations sont réservables : prix, durées, acompte, formats de réservation.',
      'Vos suppléments et votre matériel sont déclarés et pris en compte dans les disponibilités.',
      'Vous vendez des packs et des cartes cadeaux en ligne.',
    ],
    chapters: [
      { at: '00:00', label: 'Les catégories, à créer en premier', feature_refs: ['catalog.category.manage'] },
      {
        at: '01:25',
        label: 'Nommer sa prestation : le mot-clé avant le marketing',
        feature_refs: ['catalog.service.manage'],
      },
      { at: '03:26', label: 'L’adresse de la page et le référencement' },
      { at: '04:07', label: 'La description avec Pulse, les images et les vidéos' },
      { at: '04:44', label: 'Les prix : plusieurs durées, plusieurs tarifs' },
      {
        at: '06:28',
        label: 'Suppléments, couleur d’agenda et type de paiement',
        feature_refs: ['catalog.supplement.manage'],
      },
      { at: '07:51', label: 'Les formats de réservation' },
      {
        at: '10:55',
        label: 'Configurer les suppléments et le matériel',
        feature_refs: ['catalog.resource.manage'],
      },
      { at: '13:03', label: 'Créer un pack', feature_refs: ['catalog.pack.manage'] },
      { at: '15:11', label: 'Configurer les cartes cadeaux', feature_refs: ['sales.voucher.manage'] },
      { at: '16:33', label: 'Personnaliser la carte et l’intégrer à un site existant' },
    ],
  },
  {
    id: 'agenda',
    category: 'agenda',
    phase: 'setup',
    title: 'Paramétrer l’agenda et gérer vos rendez-vous',
    intent:
      'Définir vos disponibilités et vos règles de réservation, diffuser votre lien, puis gérer vos rendez-vous au quotidien.',
    duration_s: 1242,
    video: { provider: 'youtube', video_id: 'O3qFykMzCac', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Vos clients ne peuvent réserver que quand, où et pour ce que vous avez décidé.',
      'Vos règles sont posées : confirmation, délais, visibilité du planning.',
      'Vous savez créer, déplacer et annuler un rendez-vous.',
    ],
    chapters: [
      {
        at: '00:00',
        label: 'Les deux temps de l’agenda, et la navigation',
        feature_refs: ['agenda.appointment.view'],
      },
      { at: '01:38', label: 'Créer et personnaliser un planning', feature_refs: ['agenda.availability.manage'] },
      { at: '04:39', label: 'Plusieurs plannings : par membre ou par lieu' },
      { at: '05:28', label: 'Les jours fériés' },
      { at: '06:03', label: 'Bloquer et débloquer un créneau' },
      { at: '07:23', label: 'L’affichage de l’agenda', feature_refs: ['agenda.booking.settings'] },
      { at: '08:10', label: 'Confirmer les rendez-vous, délais et pénalités' },
      { at: '09:33', label: 'Horizon de réservation et fréquence des créneaux' },
      { at: '12:00', label: 'Le formulaire client et les instructions' },
      { at: '14:21', label: 'Synchroniser son agenda' },
      {
        at: '15:25',
        label: 'Partager le lien de réservation et l’intégrer à un site',
        feature_refs: ['agenda.booking.share'],
      },
      { at: '17:29', label: 'Ajouter un rendez-vous', feature_refs: ['agenda.appointment.create'] },
      {
        at: '19:26',
        label: 'Déplacer, modifier, annuler et consulter',
        feature_refs: ['agenda.appointment.update', 'agenda.appointment.cancel', 'agenda.appointment.status'],
      },
    ],
  },
  {
    id: 'website',
    category: 'website',
    phase: 'setup',
    title: 'Personnaliser votre site internet',
    intent:
      "Construire votre site avec l'éditeur visuel, le référencer localement, y brancher votre domaine et vos adresses email.",
    duration_s: 1718,
    video: { provider: 'youtube', video_id: 'TCaRNlWbw0o', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Votre site est construit et stylé sans écrire une ligne de code.',
      'Votre nom de domaine et vos adresses email professionnelles sont branchés.',
      'Vous êtes référencé sur les villes où vous voulez être trouvé.',
      'Vos blocs clés sont en place : prestations, cartes cadeaux, avis, FAQ, réservation.',
    ],
    chapters: [
      { at: '00:01', label: 'Les trois onglets du site' },
      {
        at: '00:22',
        label: 'Le titre et la description qui s’affichent sur Google',
        feature_refs: ['website.identity.manage'],
      },
      {
        at: '01:24',
        label: 'Référencement local ou national, et les zones',
        feature_refs: ['website.seo_geo.manage'],
      },
      { at: '03:22', label: 'Redirections et liens raccourcis', feature_refs: ['website.redirects.manage'] },
      { at: '04:19', label: 'Statistiques, tracking publicitaire et scripts' },
      {
        at: '04:59',
        label: 'Le domaine et les boîtes mail',
        feature_refs: ['website.domain.connect', 'website.email.configure'],
      },
      { at: '05:14', label: 'L’éditeur : ce que le site reprend tout seul', feature_refs: ['website.builder.edit'] },
      { at: '07:33', label: 'Le menu et l’ajout de pages', feature_refs: ['website.builder.pages'] },
      { at: '09:47', label: 'Les pages « zones d’intervention » et le référencement local' },
      { at: '13:24', label: 'Les pages carte cadeau et pack' },
      { at: '14:54', label: 'Le style : formes, couleurs, typographies', feature_refs: ['website.builder.style'] },
      {
        at: '15:57',
        label: 'Le bloc principal : image, diaporama ou vidéo',
        feature_refs: ['website.builder.component'],
      },
      { at: '17:37', label: 'La bibliothèque de composants' },
      { at: '21:06', label: 'Les blocs de contenu et la FAQ' },
      { at: '22:24', label: 'Les médias, et créer des sous-pages' },
      { at: '26:24', label: 'Le bouton de réservation et le parcours client' },
    ],
  },
  {
    id: 'customers',
    category: 'customers',
    phase: 'daily',
    title: 'Gérer et segmenter vos clients',
    intent: 'Importer, retrouver, filtrer et segmenter vos fiches clients — la matière première de toutes vos actions.',
    duration_s: 314,
    video: { provider: 'youtube', video_id: 'zhTJdyfwcPU', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Votre base est importée et vos doublons repérés.',
      'Vous retrouvez un client en quelques secondes grâce aux filtres.',
      'Votre base est segmentée par tags, prête pour vos campagnes.',
    ],
    chapters: [
      {
        at: '00:00',
        label: 'La section Clients et l’origine des données',
        feature_refs: ['customers.customer.read'],
      },
      {
        at: '00:18',
        label: 'Ajouter un client ou importer un fichier',
        feature_refs: ['customers.customer.create', 'customers.customer.merge'],
      },
      { at: '01:49', label: 'Retrouver et filtrer ses clients', feature_refs: ['customers.customer.read'] },
      { at: '02:06', label: 'Les tags : segmenter sa clientèle', feature_refs: ['customers.tag.manage'] },
      { at: '03:37', label: 'Modifier en masse et exporter', feature_refs: ['customers.customer.update'] },
    ],
  },
  {
    id: 'marketing-blog',
    category: 'marketing',
    subsection: 'blog',
    phase: 'daily',
    title: 'Optimiser son référencement avec le blog',
    intent:
      'Alimenter le blog de votre site avec des articles optimisés pour le référencement, rangés dans des catégories cohérentes.',
    duration_s: 437,
    video: { provider: 'youtube', video_id: 'B_319cCY5GM', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Vous comprenez ce que le blog apporte à votre référencement local.',
      'Vos articles sont rangés dans des catégories qui structurent votre blog.',
      'Vous savez publier un article ou en programmer la parution.',
    ],
    chapters: [
      { at: '00:00', label: 'Le blog comme levier de référencement' },
      { at: '01:18', label: 'Catégoriser ses articles', feature_refs: ['marketing.blog.category.manage'] },
      { at: '01:45', label: 'Rédiger un article avec Pulse', feature_refs: ['marketing.blog.article.create'] },
      {
        at: '05:18',
        label: 'Publier ou programmer, et vérifier le rendu',
        feature_refs: ['marketing.blog.article.create'],
      },
    ],
  },
  {
    id: 'marketing-campagnes',
    category: 'marketing',
    subsection: 'campagnes',
    phase: 'daily',
    title: 'Communication : configurer vos campagnes SMS et newsletters',
    intent:
      'Envoyer des campagnes email et SMS, piloter les messages automatiques, et répondre aux avis Google avec l’aide de Pulse.',
    duration_s: 726,
    video: { provider: 'youtube', video_id: 'uL-03idYev4', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Vous connaissez les types de campagnes et savez régler votre expéditeur.',
      'Vous maîtrisez le coût d’un SMS et ses limites de caractères.',
      'Vous suivez la rentabilité de vos envois grâce aux liens de suivi.',
      'Vos messages automatiques et vos réponses aux avis sont sous contrôle.',
    ],
    chapters: [
      {
        at: '00:00',
        label: 'Les types de campagnes et les paramètres à vérifier',
        feature_refs: ['marketing.communication.settings'],
      },
      {
        at: '01:27',
        label: 'Créer une campagne : nom, destinataires, expéditeur',
        feature_refs: ['marketing.campaign.sms.create', 'marketing.campaign.email.create'],
      },
      {
        at: '02:18',
        label: 'Le SMS : 160 caractères et caractères spéciaux',
        feature_refs: ['marketing.campaign.sms.create'],
      },
      { at: '03:19', label: 'Le suivi des liens et la rentabilité' },
      {
        at: '04:21',
        label: 'Aperçu, test, envoi immédiat ou programmé',
        feature_refs: ['marketing.campaign.email.schedule'],
      },
      {
        at: '04:52',
        label: 'La campagne e-mailing et son éditeur',
        feature_refs: ['marketing.campaign.email.create', 'marketing.campaign.email.compose'],
      },
      { at: '07:15', label: 'Les messages automatiques', feature_refs: ['marketing.messages.automatic'] },
      // Les avis Google et leurs réponses générées n'ont AUCUNE fiche de doc à
      // ce jour (aucun manifeste ne couvre l'onglet « Avis Google »). Chapitre
      // laissé sans `feature_refs` en attendant que la fiche existe.
      { at: '09:19', label: 'Les avis Google et les réponses générées' },
    ],
  },
  {
    id: 'sales',
    category: 'sales',
    phase: 'daily',
    title: 'Suivre les ventes, les paiements et les cartes cadeaux',
    intent: 'Consulter et piloter vos encaissements, vos empreintes bancaires, et les cartes cadeaux ou packs vendus.',
    duration_s: 169,
    video: { provider: 'youtube', video_id: 'CYZHybw7qPQ', playlist_id: 'PLKyI0s1DjJ_M' },
    outcomes: [
      'Vous retrouvez n’importe quel paiement et vous savez agir dessus.',
      'Vous suivez vos ventes de cartes cadeaux et de packs.',
    ],
    chapters: [
      // Timecodes recalés sur la transcription : les bornes auto de Loom
      // arrivaient jusqu'à 24 s APRÈS le début réel du sujet (« créer une carte
      // cadeau » annoncé à 02:17 alors qu'il démarre à 01:53).
      {
        at: '00:00',
        label: 'Le listing des paiements et les filtres',
        feature_refs: ['sales.payment.list'],
      },
      { at: '00:38', label: 'Les packs et les cartes cadeaux vendus', feature_refs: ['sales.voucher.list'] },
      { at: '01:53', label: 'Créer une carte cadeau à la main', feature_refs: ['sales.voucher.list'] },
    ],
  },
];
